import { BidAskDiffType, GreaterOrLessThan } from '../alertFilter/types'

export interface Filter {
  sectors: Sector[]
  maturities: string[]
  securityTypes: SecurityType[]
  couponTypes: CouponType[]
  currencies: Currency[]
  ratings: Rating[]
  securitiesAndTickers: string[]
  price: number | undefined
  priceGreaterLessThan: GreaterOrLessThan
  yield: number | undefined
  yieldGreaterLessThan: GreaterOrLessThan
  bidAskDiff: number | undefined
  bidAskDiffGreaterLessThan: GreaterOrLessThan
  bidAskDiffType: BidAskDiffType
  series: Series[]
  countries: string[]
  ageOfIssue: TimeFilter
}

export enum Series {
  REGS = 1,
  _144a = 2,
  Registered = 3
}

export const AllSeries = [
  { label: 'REGS', value: Series.REGS },
  { label: '144a', value: Series._144a },
  { label: 'Registered', value: Series.Registered }
]

export const SeriesMap = AllSeries.reduce<Record<string, string>>(
  (acc, { value, label }) => {
    acc[value] = label
    return acc
  },
  {}
)

export enum Sector {
  BasicMaterials = 387,
  Communications = 386,
  ConsumerCyclical = 388,
  ConsumerNonCyclical = 390,
  Energy = 391,
  Diversified = 392,
  EGB = 394,
  Financial = 384,
  Government = 395,
  Industrial = 385,
  Technology = 393,
  TestingDemoSecurities = 379,
  UST = 365,
  Utilities = 389
}
export const MaturitiesValues = [
  '1yr',
  '2yr',
  '3yr',
  '4yr',
  '5yr',
  '6yr',
  '7yr',
  '8yr',
  '9yr',
  '10yr',
  '>10yr'
]
export const MaturityGroups = [
  { label: '<5yr', values: ['1yr', '2yr', '3yr', '4yr'] },
  { label: '5yr+', values: ['5yr', '6yr', '7yr', '8yr', '9yr', '10yr'] },
  { label: '>10yr', values: ['>10yr'] }
]
export enum SecurityType {
  Corporate = 0,
  US_Government,
  European_Government,
  Other
}

export const AllSecurityTypes = [
  SecurityType.Corporate,
  SecurityType.US_Government,
  SecurityType.European_Government,
  SecurityType.Other
]

export const SecurityTypeValues = [
  'Corporate',
  'US Government',
  'European Government',
  'Other'
]

export enum CouponType {
  Floating_Rate = 0,
  Non_Floating_Rate
}

export const AllCouponTypes = [
  CouponType.Floating_Rate,
  CouponType.Non_Floating_Rate
]

export const CouponTypeValues = ['Floating Rate', 'Non Floating Rate']

export type Currency =
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'CAD'
  | 'AUD'
  | 'SGD'
  | 'SEK'
  | 'NOK'
  | 'CHF'
  | 'ZAR'
  | 'HKD'
  | 'CNH'
  | 'NZD'
  | 'TRY'
  | 'MXN'
  | 'DKK'
  | 'CZK'
  | 'JPY'
  | 'ARS'
  | 'BRL'
  | 'COP'
export const CurrencyValues = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'SGD',
  'SEK',
  'NOK',
  'CHF',
  'ZAR',
  'HKD',
  'CNH',
  'NZD',
  'TRY',
  'MXN',
  'DKK',
  'CZK',
  'JPY',
  'ARS',
  'BRL',
  'COP'
] satisfies Currency[]

export const Sectors = {
  387: 'Basic Materials',
  386: 'Communications',
  388: 'Consumer Cyclical',
  390: 'Consumer Non-Cyclical',
  391: 'Energy',
  392: 'Diversified',
  394: 'EGB',
  384: 'Financial',
  395: 'Government',
  385: 'Industrial',
  393: 'Technology',
  379: 'Testing/Demo Securities',
  365: 'UST',
  389: 'Utilities'
}

export const AllSectors = [
  Sector.BasicMaterials,
  Sector.Communications,
  Sector.ConsumerCyclical,
  Sector.ConsumerNonCyclical,
  Sector.Energy,
  Sector.Diversified,
  Sector.EGB,
  Sector.Financial,
  Sector.Government,
  Sector.Industrial,
  Sector.Technology,
  Sector.TestingDemoSecurities,
  Sector.UST,
  Sector.Utilities
]

export enum Rating {
  AAA = 0,
  AAPlus,
  AA,
  AAMinus,
  APlus,
  A,
  AMinus,
  BBBPlus,
  BBB,
  BBBMinus,
  TBD,
  BBPlus,
  BB,
  BBMinus,
  BPlus,
  B,
  BMinus,
  CCCPlus,
  CCC,
  CCCMinus,
  CC,
  C,
  CI,
  R,
  SD,
  D,
  NR
}

export const AllRatings = [
  Rating.AAA,
  Rating.AAPlus,
  Rating.AA,
  Rating.AAMinus,
  Rating.APlus,
  Rating.A,
  Rating.AMinus,
  Rating.BBBPlus,
  Rating.BBB,
  Rating.BBBMinus,
  Rating.TBD,
  Rating.BBPlus,
  Rating.BB,
  Rating.BBMinus,
  Rating.BPlus,
  Rating.B,
  Rating.BMinus,
  Rating.CCCPlus,
  Rating.CCC,
  Rating.CCCMinus,
  Rating.CC,
  Rating.C,
  Rating.CI,
  Rating.R,
  Rating.SD,
  Rating.D,
  Rating.NR
]

export const RatingsGroups = [
  {
    label: 'IG',
    values: [
      'AAA',
      'AA+',
      'AA',
      'AA-',
      'A+',
      'A',
      'A-',
      'BBB+',
      'BBB',
      'BBB-',
      'TBD'
    ]
  },
  {
    label: 'HY',
    values: [
      'BB+',
      'BB',
      'BB-',
      'B+',
      'B',
      'B-',
      'CCC+',
      'CCC',
      'CCC-',
      'CC',
      'C',
      'CI',
      'R',
      'SD',
      'D',
      'NR'
    ]
  }
] as const

// The S&P rating scale is fixed; the union lets callers reject invalid ratings
export type SAndPRating = (typeof RatingsGroups)[number]['values'][number]

export const IGRatingValues: readonly string[] = RatingsGroups[0].values
export const AllRatingValues = RatingsGroups.reduce(
  (acc, group) => acc.concat(group.values),
  [] as string[]
)

export const Countries: Record<string, string> = {
  US: 'United States of America (US)',
  AR: 'Argentina (AR)',
  BR: 'Brazil (BR)',
  CA: 'Canada (CA)',
  CN: 'China (CN)',
  FR: 'France (FR)',
  DE: 'Germany (DE)',
  IN: 'India (IN)',
  MX: 'Mexico (MX)',
  GB: 'United Kingdom (GB)',
  AF: 'Afghanistan (AF)',
  AL: 'Albania (AL)',
  DZ: 'Algeria (DZ)',
  AS: 'American Samoa (AS)',
  AD: 'Andorra (AD)',
  AO: 'Angola (AO)',
  AI: 'Anguilla (AI)',
  AQ: 'Antarctica (AQ)',
  AG: 'Antigua and Barbuda (AG)',
  AM: 'Armenia (AM)',
  AW: 'Aruba (AW)',
  AU: 'Australia (AU)',
  AT: 'Austria (AT)',
  AZ: 'Azerbaijan (AZ)',
  BS: 'Bahamas (BS)',
  BH: 'Bahrain (BH)',
  BD: 'Bangladesh (BD)',
  BB: 'Barbados (BB)',
  BY: 'Belarus (BY)',
  BE: 'Belgium (BE)',
  BZ: 'Belize (BZ)',
  BJ: 'Benin (BJ)',
  BM: 'Bermuda (BM)',
  BT: 'Bhutan (BT)',
  BO: 'Bolivia (BO)',
  BQ: 'Bonaire, Sint Eustatius and Saba (BQ)',
  BA: 'Bosnia and Herzegovina (BA)',
  BW: 'Botswana (BW)',
  BV: 'Bouvet Island (BV)',
  IO: 'British Indian Ocean Territory (IO)',
  BN: 'Brunei Darussalam (BN)',
  BG: 'Bulgaria (BG)',
  BF: 'Burkina Faso (BF)',
  BI: 'Burundi (BI)',
  CV: 'Cabo Verde (CV)',
  KH: 'Cambodia (KH)',
  CM: 'Cameroon (CM)',
  KY: 'Cayman Islands (KY)',
  CF: 'Central African Republic (CF)',
  TD: 'Chad (TD)',
  CL: 'Chile (CL)',
  CX: 'Christmas Island (CX)',
  CC: 'Cocos Islands (CC)',
  CO: 'Colombia (CO)',
  KM: 'Comoros (KM)',
  CD: 'Democratic Republic of Congo (CD)',
  CG: 'Congo (CG)',
  CK: 'Cook Islands (CK)',
  CR: 'Costa Rica (CR)',
  HR: 'Croatia (HR)',
  CU: 'Cuba (CU)',
  CW: 'Curaçao (CW)',
  CY: 'Cyprus (CY)',
  CZ: 'Czechia (CZ)',
  CI: "Côte d'Ivoire (CI)",
  DK: 'Denmark (DK)',
  DJ: 'Djibouti (DJ)',
  DM: 'Dominica (DM)',
  DO: 'Dominican Republic (DO)',
  EC: 'Ecuador (EC)',
  EG: 'Egypt (EG)',
  SV: 'El Salvador (SV)',
  GQ: 'Equatorial Guinea (GQ)',
  ER: 'Eritrea (ER)',
  EE: 'Estonia (EE)',
  SZ: 'Eswatini (SZ)',
  ET: 'Ethiopia (ET)',
  FK: 'Falkland Islands (FK)',
  FO: 'Faroe Islands (FO)',
  FJ: 'Fiji (FJ)',
  FI: 'Finland (FI)',
  GF: 'French Guiana (GF)',
  PF: 'French Polynesia (PF)',
  TF: 'French Southern Territories (TF)',
  GA: 'Gabon (GA)',
  GM: 'Gambia (GM)',
  GE: 'Georgia (GE)',
  GH: 'Ghana (GH)',
  GI: 'Gibraltar (GI)',
  GR: 'Greece (GR)',
  GL: 'Greenland (GL)',
  GD: 'Grenada (GD)',
  GP: 'Guadeloupe (GP)',
  GU: 'Guam (GU)',
  GT: 'Guatemala (GT)',
  GG: 'Guernsey (GG)',
  GN: 'Guinea (GN)',
  GW: 'Guinea-Bissau (GW)',
  GY: 'Guyana (GY)',
  HT: 'Haiti (HT)',
  HM: 'Heard Island and McDonald Islands (HM)',
  VA: 'Vatican (VA)',
  HN: 'Honduras (HN)',
  HK: 'Hong Kong (HK)',
  HU: 'Hungary (HU)',
  IS: 'Iceland (IS)',
  ID: 'Indonesia (ID)',
  IR: 'Iran (IR)',
  IQ: 'Iraq (IQ)',
  IE: 'Ireland (IE)',
  IM: 'Isle of Man (IM)',
  IL: 'Israel (IL)',
  IT: 'Italy (IT)',
  JM: 'Jamaica (JM)',
  JP: 'Japan (JP)',
  JE: 'Jersey (JE)',
  JO: 'Jordan (JO)',
  KZ: 'Kazakhstan (KZ)',
  KE: 'Kenya (KE)',
  KI: 'Kiribati (KI)',
  KP: 'North Korea (KP)',
  KR: 'South Korea (KR)',
  KW: 'Kuwait (KW)',
  KG: 'Kyrgyzstan (KG)',
  LA: 'Laos (LA)',
  LV: 'Latvia (LV)',
  LB: 'Lebanon (LB)',
  LS: 'Lesotho (LS)',
  LR: 'Liberia (LR)',
  LY: 'Libya (LY)',
  LI: 'Liechtenstein (LI)',
  LT: 'Lithuania (LT)',
  LU: 'Luxembourg (LU)',
  MO: 'Macao (MO)',
  MG: 'Madagascar (MG)',
  MW: 'Malawi (MW)',
  MY: 'Malaysia (MY)',
  MV: 'Maldives (MV)',
  ML: 'Mali (ML)',
  MT: 'Malta (MT)',
  MH: 'Marshall Islands (MH)',
  MQ: 'Martinique (MQ)',
  MR: 'Mauritania (MR)',
  MU: 'Mauritius (MU)',
  YT: 'Mayotte (YT)',
  FM: 'Micronesia (FM)',
  MD: 'Moldova (MD)',
  MC: 'Monaco (MC)',
  MN: 'Mongolia (MN)',
  ME: 'Montenegro (ME)',
  MS: 'Montserrat (MS)',
  MA: 'Morocco (MA)',
  MZ: 'Mozambique (MZ)',
  MM: 'Myanmar (MM)',
  NA: 'Namibia (NA)',
  NR: 'Nauru (NR)',
  NP: 'Nepal (NP)',
  NL: 'Netherlands (NL)',
  NC: 'New Caledonia (NC)',
  NZ: 'New Zealand (NZ)',
  NI: 'Nicaragua (NI)',
  NE: 'Niger (NE)',
  NG: 'Nigeria (NG)',
  NU: 'Niue (NU)',
  NF: 'Norfolk Island (NF)',
  MP: 'Northern Mariana Islands (MP)',
  NO: 'Norway (NO)',
  OM: 'Oman (OM)',
  PK: 'Pakistan (PK)',
  PW: 'Palau (PW)',
  PS: 'Palestine (PS)',
  PA: 'Panama (PA)',
  PG: 'Papua New Guinea (PG)',
  PY: 'Paraguay (PY)',
  PE: 'Peru (PE)',
  PH: 'Philippines (PH)',
  PN: 'Pitcairn (PN)',
  PL: 'Poland (PL)',
  PT: 'Portugal (PT)',
  PR: 'Puerto Rico (PR)',
  QA: 'Qatar (QA)',
  MK: 'North Macedonia (MK)',
  RO: 'Romania (RO)',
  RU: 'Russia (RU)',
  RW: 'Rwanda (RW)',
  RE: 'Réunion (RE)',
  BL: 'Saint Barthélemy (BL)',
  SH: 'Saint Helena (SH)',
  KN: 'Saint Kitts and Nevis (KN)',
  LC: 'Saint Lucia (LC)',
  MF: 'Saint Martin (MF)',
  PM: 'Saint Pierre and Miquelon (PM)',
  VC: 'Saint Vincent and the Grenadines (VC)',
  WS: 'Samoa (WS)',
  SM: 'San Marino (SM)',
  ST: 'Sao Tome and Principe (ST)',
  SA: 'Saudi Arabia (SA)',
  SN: 'Senegal (SN)',
  RS: 'Serbia (RS)',
  SC: 'Seychelles (SC)',
  SL: 'Sierra Leone (SL)',
  SG: 'Singapore (SG)',
  SX: 'Sint Maarten (SX)',
  SK: 'Slovakia (SK)',
  SI: 'Slovenia (SI)',
  SB: 'Solomon Islands (SB)',
  SO: 'Somalia (SO)',
  ZA: 'South Africa (ZA)',
  GS: 'South Georgia and the South Sandwich Islands (GS)',
  SS: 'South Sudan (SS)',
  ES: 'Spain (ES)',
  LK: 'Sri Lanka (LK)',
  SD: 'Sudan (SD)',
  SR: 'Suriname (SR)',
  SJ: 'Svalbard and Jan Mayen (SJ)',
  SE: 'Sweden (SE)',
  CH: 'Switzerland (CH)',
  SY: 'Syrian Arab Republic (SY)',
  TW: 'Taiwan (TW)',
  TJ: 'Tajikistan (TJ)',
  TZ: 'Tanzania (TZ)',
  TH: 'Thailand (TH)',
  TL: 'Timor-Leste (TL)',
  TG: 'Togo (TG)',
  TK: 'Tokelau (TK)',
  TO: 'Tonga (TO)',
  TT: 'Trinidad and Tobago (TT)',
  TN: 'Tunisia (TN)',
  TR: 'Turkey (TR)',
  TM: 'Turkmenistan (TM)',
  TC: 'Turks and Caicos (TC)',
  TV: 'Tuvalu (TV)',
  UG: 'Uganda (UG)',
  UA: 'Ukraine (UA)',
  AE: 'United Arab Emirates (AE)',
  UM: 'United States Minor Outlying Islands (UM)',
  UY: 'Uruguay (UY)',
  UZ: 'Uzbekistan (UZ)',
  VU: 'Vanuatu (VU)',
  VE: 'Venezuela (VE)',
  VN: 'Vietnam (VN)',
  VG: 'British Virgin Islands (VG)',
  VI: 'US Virgin Islands (VI)',
  WF: 'Wallis and Futuna (WF)',
  EH: 'Western Sahara (EH)',
  YE: 'Yemen (YE)',
  ZM: 'Zambia (ZM)',
  ZW: 'Zimbabwe (ZW)',
  AX: 'Åland Islands (AX)',
  SNAT: 'Supernational (SNAT)',
  OUT: 'Other (OUT)',
  MUL: 'Multinational (MUL)'
}

export const CountryNameToCode = Object.entries(Countries).reduce(
  (acc, [code, name]) => {
    acc[name] = code
    return acc
  },
  {} as Record<string, string>
)

export const RegionGroups = [
  {
    label: 'North America',
    values: [
      'United States of America (US)',
      'Canada (CA)',
      'Mexico (MX)',
      'Bermuda (BM)',
      'Cayman Islands (KY)',
      'British Virgin Islands (VG)'
    ]
  },
  {
    label: 'Europe',
    values: [
      'Austria (AT)',
      'Belgium (BE)',
      'Bulgaria (BG)',
      'Switzerland (CH)',
      'Cyprus (CY)',
      'Czechia (CZ)',
      'Germany (DE)',
      'Denmark (DK)',
      'Spain (ES)',
      'Finland (FI)',
      'France (FR)',
      'United Kingdom (GB)',
      'Guernsey (GG)',
      'Greece (GR)',
      'Croatia (HR)',
      'Hungary (HU)',
      'Ireland (IE)',
      'Iceland (IS)',
      'Italy (IT)',
      'Jersey (JE)',
      'Liechtenstein (LI)',
      'Lithuania (LT)',
      'Luxembourg (LU)',
      'Latvia (LV)',
      'Malta (MT)',
      'Netherlands (NL)',
      'Norway (NO)',
      'Poland (PL)',
      'Portugal (PT)',
      'Romania (RO)',
      'Serbia (RS)',
      'Sweden (SE)',
      'Slovenia (SI)',
      'Slovakia (SK)',
      'Turkey (TR)'
    ]
  },
  {
    label: 'Latin America',
    values: [
      'Argentina (AR)',
      'Brazil (BR)',
      'Chile (CL)',
      'Colombia (CO)',
      'Costa Rica (CR)',
      'Dominican Republic (DO)',
      'Ecuador (EC)',
      'El Salvador (SV)',
      'Grenada (GD)',
      'Guatemala (GT)',
      'Panama (PA)',
      'Peru (PE)',
      'Venezuela (VE)'
    ]
  },
  {
    label: 'Asia-Pacific',
    values: [
      'Australia (AU)',
      'China (CN)',
      'Hong Kong (HK)',
      'Indonesia (ID)',
      'India (IN)',
      'Japan (JP)',
      'South Korea (KR)',
      'Sri Lanka (LK)',
      'Mongolia (MN)',
      'Malaysia (MY)',
      'New Zealand (NZ)',
      'Philippines (PH)',
      'Pakistan (PK)',
      'Singapore (SG)',
      'Thailand (TH)',
      'Taiwan (TW)'
    ]
  },
  {
    label: 'Middle East',
    values: [
      'United Arab Emirates (AE)',
      'Bahrain (BH)',
      'Israel (IL)',
      'Jordan (JO)',
      'Kuwait (KW)',
      'Lebanon (LB)',
      'Qatar (QA)',
      'Saudi Arabia (SA)'
    ]
  },
  {
    label: 'Africa',
    values: [
      'Angola (AO)',
      'Ethiopia (ET)',
      'Gabon (GA)',
      'Ghana (GH)',
      'Kenya (KE)',
      'Liberia (LR)',
      'Morocco (MA)',
      'Mauritius (MU)',
      'Senegal (SN)',
      'Togo (TG)',
      'Tunisia (TN)',
      'South Africa (ZA)',
      'Zambia (ZM)'
    ]
  },
  {
    label: 'Emerging Markets',
    values: [
      'Belarus (BY)',
      'Georgia (GE)',
      'Kazakhstan (KZ)',
      'Russia (RU)',
      'Ukraine (UA)',
      'Uzbekistan (UZ)',
      'Marshall Islands (MH)',
      'Papua New Guinea (PG)'
    ]
  },
  {
    label: 'Supernational',
    values: ['Supernational (SNAT)']
  },
  {
    label: 'Other',
    values: [
      'Afghanistan (AF)',
      'Albania (AL)',
      'Algeria (DZ)',
      'American Samoa (AS)',
      'Andorra (AD)',
      'Anguilla (AI)',
      'Antarctica (AQ)',
      'Antigua and Barbuda (AG)',
      'Armenia (AM)',
      'Aruba (AW)',
      'Azerbaijan (AZ)',
      'Bahamas (BS)',
      'Bangladesh (BD)',
      'Barbados (BB)',
      'Belize (BZ)',
      'Benin (BJ)',
      'Bhutan (BT)',
      'Bolivia (BO)',
      'Bonaire, Sint Eustatius and Saba (BQ)',
      'Bosnia and Herzegovina (BA)',
      'Botswana (BW)',
      'Bouvet Island (BV)',
      'British Indian Ocean Territory (IO)',
      'Brunei Darussalam (BN)',
      'Burkina Faso (BF)',
      'Burundi (BI)',
      'Cabo Verde (CV)',
      'Cambodia (KH)',
      'Cameroon (CM)',
      'Central African Republic (CF)',
      'Chad (TD)',
      'Christmas Island (CX)',
      'Cocos Islands (CC)',
      'Comoros (KM)',
      'Democratic Republic of Congo (CD)',
      'Congo (CG)',
      'Cook Islands (CK)',
      'Cuba (CU)',
      'Curaçao (CW)',
      "Côte d'Ivoire (CI)",
      'Djibouti (DJ)',
      'Dominica (DM)',
      'Egypt (EG)',
      'Equatorial Guinea (GQ)',
      'Eritrea (ER)',
      'Estonia (EE)',
      'Eswatini (SZ)',
      'Falkland Islands (FK)',
      'Faroe Islands (FO)',
      'Fiji (FJ)',
      'French Guiana (GF)',
      'French Polynesia (PF)',
      'French Southern Territories (TF)',
      'Gambia (GM)',
      'Gibraltar (GI)',
      'Greenland (GL)',
      'Guadeloupe (GP)',
      'Guam (GU)',
      'Guinea (GN)',
      'Guinea-Bissau (GW)',
      'Guyana (GY)',
      'Haiti (HT)',
      'Heard Island and McDonald Islands (HM)',
      'Vatican (VA)',
      'Honduras (HN)',
      'Iran (IR)',
      'Iraq (IQ)',
      'Isle of Man (IM)',
      'Jamaica (JM)',
      'North Korea (KP)',
      'Kiribati (KI)',
      'Kyrgyzstan (KG)',
      'Laos (LA)',
      'Lesotho (LS)',
      'Libya (LY)',
      'Macao (MO)',
      'Madagascar (MG)',
      'Malawi (MW)',
      'Maldives (MV)',
      'Mali (ML)',
      'Martinique (MQ)',
      'Mauritania (MR)',
      'Mayotte (YT)',
      'Micronesia (FM)',
      'Moldova (MD)',
      'Monaco (MC)',
      'Montenegro (ME)',
      'Montserrat (MS)',
      'Mozambique (MZ)',
      'Multinational (MUL)',
      'Myanmar (MM)',
      'Namibia (NA)',
      'Nauru (NR)',
      'Nepal (NP)',
      'New Caledonia (NC)',
      'Nicaragua (NI)',
      'Niger (NE)',
      'Nigeria (NG)',
      'Niue (NU)',
      'Norfolk Island (NF)',
      'Northern Mariana Islands (MP)',
      'Oman (OM)',
      'Other (OUT)',
      'Palau (PW)',
      'Palestine (PS)',
      'Paraguay (PY)',
      'Pitcairn (PN)',
      'Puerto Rico (PR)',
      'North Macedonia (MK)',
      'Rwanda (RW)',
      'Réunion (RE)',
      'Saint Barthélemy (BL)',
      'Saint Helena (SH)',
      'Saint Kitts and Nevis (KN)',
      'Saint Lucia (LC)',
      'Saint Martin (MF)',
      'Saint Pierre and Miquelon (PM)',
      'Saint Vincent and the Grenadines (VC)',
      'Samoa (WS)',
      'San Marino (SM)',
      'Sao Tome and Principe (ST)',
      'Seychelles (SC)',
      'Sierra Leone (SL)',
      'Sint Maarten (SX)',
      'Solomon Islands (SB)',
      'Somalia (SO)',
      'South Georgia and the South Sandwich Islands (GS)',
      'South Sudan (SS)',
      'Sudan (SD)',
      'Suriname (SR)',
      'Svalbard and Jan Mayen (SJ)',
      'Syrian Arab Republic (SY)',
      'Tajikistan (TJ)',
      'Tanzania (TZ)',
      'Timor-Leste (TL)',
      'Tokelau (TK)',
      'Tonga (TO)',
      'Trinidad and Tobago (TT)',
      'Turkmenistan (TM)',
      'Turks and Caicos (TC)',
      'Tuvalu (TV)',
      'Uganda (UG)',
      'United States Minor Outlying Islands (UM)',
      'Uruguay (UY)',
      'Vanuatu (VU)',
      'Vietnam (VN)',
      'US Virgin Islands (VI)',
      'Wallis and Futuna (WF)',
      'Western Sahara (EH)',
      'Yemen (YE)',
      'Zimbabwe (ZW)',
      'Åland Islands (AX)'
    ]
  }
]

export const CountryCodes = RegionGroups.flatMap((region) =>
  region.values.map((country) =>
    country.slice(country.indexOf('(') + 1, country.indexOf(')'))
  )
)

export const CountryNames = RegionGroups.flatMap((region) => region.values)

// Lookup from country code to region label
export const CountryCodeToRegion: Record<string, string> = Object.keys(
  Countries
).reduce((acc, code) => {
  const countryName = Countries[code]
  const region = RegionGroups.find((group) =>
    group.values.includes(countryName)
  )
  acc[code] = region?.label ?? 'Other'
  return acc
}, {} as Record<string, string>)

export enum TimeFilter {
  Any = 0,
  OneDay = 1,
  TwoDay = 2,
  Week = 7,
  Month = 30,
  HalfYear = 180,
  Year = 365,
  GTYear = 366
}

export const timeFilterOptions = [
  { label: 'Any time', value: TimeFilter.Any },
  { label: '1 day', value: TimeFilter.OneDay },
  { label: '2 days', value: TimeFilter.TwoDay },
  { label: '1 week', value: TimeFilter.Week },
  { label: '1 month', value: TimeFilter.Month },
  { label: '6 months', value: TimeFilter.HalfYear },
  { label: '1 year', value: TimeFilter.Year },
  { label: '>1 year', value: TimeFilter.GTYear }
]

export const AllSizes = [
  { label: 'Any', value: 0 },
  { label: '200k', value: 200 },
  { label: '500k', value: 500 },
  { label: '1000k', value: 1000 },
  { label: '2000k', value: 2000 },
  { label: 'Custom', value: 0 }
]

export const defaultFilter = {
  sectors: AllSectors,
  currencies: CurrencyValues as Currency[],
  maturities: MaturitiesValues,
  ratings: AllRatings,
  countries: CountryCodes,
  couponTypes: AllCouponTypes,
  securityTypes: AllSecurityTypes,
  securitiesAndTickers: [],
  price: undefined,
  priceGreaterLessThan: GreaterOrLessThan.GreaterThan,
  yield: undefined,
  yieldGreaterLessThan: GreaterOrLessThan.GreaterThan,
  bidAskDiff: undefined,
  bidAskDiffGreaterLessThan: GreaterOrLessThan.LessThan,
  bidAskDiffType: BidAskDiffType.Price,
  series: AllSeries.map((s) => s.value),
  ageOfIssue: TimeFilter.Any
}

export const OPTIONAL_FILTERS = [
  { key: 'myFirm', label: 'My Firm' },
  { key: 'myBook', label: 'My Book' },
  { key: 'age', label: 'Time Since Issuance' },
  { key: 'size', label: 'Size' },
  { key: 'yield', label: 'Yield' },
  { key: 'price', label: 'Price' },
  { key: 'bidAskDiff', label: 'Bid Ask Diff' }
] as const

export type OptionalKey = (typeof OPTIONAL_FILTERS)[number]['key']
